import { Button, Modal, Typography } from '@globalmed/atlas-ui';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderSignature } from '../FormBuilderSignature';
import { formBuilderSignatureConfig } from '../constants';
import styles from './FormBuilderSignatureModal.module.css';
import {
  ESignatureSettingsRequiredBy,
  ESignatureSettingsType,
  TDocumentUserType,
  TFormBuilderContent,
  TSignatureSettingsCamel,
} from '@/types';
import { isValueOfEnum } from '@/utils/isValueOfEnum';

type TProps = {
  open: boolean;
  title: ReactNode;
  defaultType: ESignatureSettingsType;
  defaultRequiredBy: ESignatureSettingsRequiredBy;
  userType?: TDocumentUserType;
  contentMode?: TFormBuilderContent;
  onClose: () => void;
  onSave?: (signatureSettings: TSignatureSettingsCamel) => void;
};

/**
 * Modal component for editing signature settings
 * @param props - Component props
 * @param props.open - Whether the modal is open
 * @param props.title - Modal title content
 * @param props.defaultType - Default signature type
 * @param props.userType - User type
 * @param props.defaultRequiredBy - Default required participants
 * @param props.contentMode - Which builder this belongs to, deciding the
 * signature types offered and whether participants can be picked
 * @param props.onClose - Callback when modal is closed
 * @param [props.onSave] - Optional callback when settings are saved
 * @returns Form builder signature modal component
 */
export const FormBuilderSignatureModal = ({
  open,
  title,
  defaultType,
  defaultRequiredBy,
  userType = 'patient',
  contentMode = 'formBuilder',
  onClose,
  onSave,
}: TProps) => {
  const { t } = useTranslation();
  const {
    allowedTypes,
    withRequiredBy,
    defaultRequiredBy: fixedRequiredBy,
  } = formBuilderSignatureConfig[contentMode];
  const [type, setType] = useState<ESignatureSettingsType>(defaultType);
  const [requiredBy, setRequiredBy] =
    useState<ESignatureSettingsRequiredBy>(defaultRequiredBy);

  // When the participants cannot be picked, the value is fixed by the builder.
  const fallbackRequiredBy = !withRequiredBy
    ? fixedRequiredBy
    : userType === 'provider'
      ? ESignatureSettingsRequiredBy.PROVIDER
      : ESignatureSettingsRequiredBy.PATIENT;

  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    const initialRequiredBy =
      withRequiredBy && userType !== 'provider'
        ? defaultRequiredBy
        : fallbackRequiredBy;
    setRequiredBy(initialRequiredBy);
  }, [
    defaultRequiredBy,
    defaultType,
    fallbackRequiredBy,
    open,
    userType,
    withRequiredBy,
  ]);

  const handleSignatureTypeChange = useCallback(
    (value: string) => {
      if (!isValueOfEnum(value, ESignatureSettingsType)) return;
      setType(value);
      if (value === ESignatureSettingsType.NO_SIGNATURE) {
        setRequiredBy(fallbackRequiredBy);
      }
    },
    [fallbackRequiredBy],
  );

  const handleSignatureRequiredByChange = useCallback((value: string) => {
    if (isValueOfEnum(value, ESignatureSettingsRequiredBy)) {
      setRequiredBy(value);
    }
  }, []);

  const handleSave = useCallback(() => {
    onSave?.({
      type,
      requiredBy,
    });
    onClose();
  }, [onClose, onSave, requiredBy, type]);

  return (
    <Modal
      open={open}
      size="small"
      onOpenChange={onClose}
      title={
        <div>
          <Typography
            fontSize="20px"
            fontWeight="600"
            sx={{ lineHeight: '30px', marginBottom: '4px' }}
          >
            {t('common.signature-settings')}
          </Typography>
          <Typography
            color={'var(--colors-gray600)'}
            sx={{ lineHeight: '20px' }}
          >
            {title}
          </Typography>
        </div>
      }
      body={
        <div className={styles.container}>
          <FormBuilderSignature
            type={type}
            requiredBy={requiredBy}
            userType={userType}
            allowedTypes={allowedTypes}
            withRequiredBy={withRequiredBy}
            onSignatureRequiredByChange={handleSignatureRequiredByChange}
            onSignatureTypeChange={handleSignatureTypeChange}
          />
          <div className={styles.btns}>
            <Button text={t('cancel')} variant="secondary" onClick={onClose} />
            <Button text={t('common.save')} onClick={handleSave} />
          </div>
        </div>
      }
    />
  );
};
